import type { ReactElement, ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useAuth } from "@/core/auth/auth-provider";
import { buildReturnToFromLocation, normalizeReturnTo } from "@/core/auth/return-to";
import { hasAnyLiteralRole, hasAnyRequiredRole } from "@/core/auth/role-access";
import { Alert, AlertDescription, AlertTitle } from "@/shared/components/ui";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import type { RoleClaim } from "@/types/clm-web";

export interface ProtectedRouteProps {
  requiredRoles?: readonly RoleClaim[];
  /**
   * "literal" matches requiredRoles against the projected claims with none of
   * hasRole's expansions (admin ⇒ owner/super_admin), mirroring server gates
   * that check a literal operator claim. Defaults to "expanded".
   */
  roleMatch?: "expanded" | "literal";
  /**
   * 006-9 UI contract §0 (no-unmount): surfaces that obtain the bearer inside
   * the mounted component keep their children mounted while the session is
   * `refreshing` and render an inline progress cue instead of the route-level
   * status message. `loading` and `returning` are unaffected — no session
   * snapshot exists yet to render children against.
   */
  keepMountedWhileRefreshing?: boolean;
  /**
   * Where the refreshing cue renders when `keepMountedWhileRefreshing` holds.
   * A layout-level guard passes "none" so the nested surface guard owns the
   * single visible cue (no duplicate status regions).
   */
  refreshCue?: "inline" | "none";
  children?: ReactNode;
}

export function ProtectedRoute({
  requiredRoles = ["authenticated"],
  roleMatch = "expanded",
  keepMountedWhileRefreshing = false,
  refreshCue = "inline",
  children,
}: ProtectedRouteProps): ReactElement {
  const auth = useAuth();
  const location = useLocation();
  const refreshingInPlace = keepMountedWhileRefreshing && auth.status === "refreshing";

  if (auth.status === "logout") {
    return (
      <RouteStatusMessage
        title="Signing you out"
        description="Completing secure sign-out with the identity provider."
      />
    );
  }

  if (
    auth.status === "loading" ||
    (auth.status === "refreshing" && !refreshingInPlace) ||
    auth.status === "returning"
  ) {
    return (
      <RouteStatusMessage
        title="Checking session"
        description="Validating your CLM Web session before showing protected content."
      />
    );
  }

  if (!auth.session?.authenticated) {
    const returnTo = normalizeReturnTo(buildReturnToFromLocation(location));
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  const admits = roleMatch === "literal" ? hasAnyLiteralRole : hasAnyRequiredRole;
  if (!admits(requiredRoles, auth.effectiveRoleClaims)) {
    return <AccessDenied requiredRoles={requiredRoles} />;
  }

  return (
    <>
      {refreshingInPlace && refreshCue === "inline" ? <InlineRefreshCue /> : null}
      {children ?? <Outlet />}
    </>
  );
}

function InlineRefreshCue(): ReactElement {
  return (
    <div
      role="status"
      aria-busy="true"
      className="mb-4 flex items-center gap-2 text-sm text-muted-foreground"
      data-testid="protected-route-refresh-cue"
    >
      <ClmIcon name="loading" decorative className="h-4 w-4 animate-spin text-clm-brand-blue" />
      <span>Refreshing your session…</span>
    </div>
  );
}

function AccessDenied({ requiredRoles }: { requiredRoles: readonly RoleClaim[] }): ReactElement {
  return (
    <div className="mx-auto max-w-3xl">
      <Alert variant="destructive">
        {/* Sole heading of this route view: h1 matches the sibling
            RouteStatusMessage full-route states. */}
        <AlertTitle as="h1">Access denied</AlertTitle>
        <AlertDescription>
          This protected route requires one of these roles: {requiredRoles.join(", ")}. Navigation
          hiding is not security; direct URL access is still checked here.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function RouteStatusMessage({
  title,
  description,
}: {
  title: string;
  description: string;
}): ReactElement {
  return (
    // Not a <main>: nested route guards render inside AppLayout's main landmark.
    <div className="clm-page-gutter clm-section-spacing min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-3xl rounded-lg border bg-card p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-clm-primary-blue">{title}</h1>
        <p className="mt-3 text-muted-foreground">{description}</p>
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <ClmIcon name="loading" decorative className="h-6 w-6 animate-spin text-clm-brand-blue" />
          <span>{title}</span>
        </div>
      </div>
    </div>
  );
}
